import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import { ResultUploader } from "../components/results/ResultUploader";
import { Customer } from "../components/shared/interfaces/Customer";
import { CustomerDropdown } from "../components/CustomerDropdown";
import { Snackbar } from "../components/shared/Snackbar";
import { Toolbar } from "primereact/toolbar";
import { TabView } from "../components/shared/TabView";
import RouteBreadcrumbs from "../components/shared/RouteBreadcrumbs";
import { ResultListRequestType } from "../components/shared/apiHandlers/ResultsAPIs";
import Alert, { AlertColor } from "@mui/material/Alert";
import { useCompaniesByCustomerId } from "../components/data/useCompaniesByCustomerId";
import { useSitesByCompanyId } from "../components/data/useSitesByCompanyId";
import { PersistedCompanyDropdown } from "../components/PersistedCompanyDropdown";
import { PersistedSiteDropdown } from "../components/PersistedSiteDropdown";
import { TabLabels } from "../components/shared/enums/Results";
import { LOCALSTORAGE_KEYS } from "../components/shared/constants";
import { DataTableMultiSortMetaType, DataTablePFSEvent, DataTableSortOrderType } from "primereact/datatable";
import { INITIAL_LAZY_STATE } from "../components/shared/constants/LazyStateConstants";
import { AllResults } from "../components/results/AllResults";
import { UserResults } from "../components/results/UserResults";
import { ReportValues } from "../components/shared/interfaces/ReportingPayload";
import { ReportModalData } from "../components/results/ReportModalData";

const sortFieldStorageKey = LOCALSTORAGE_KEYS.resultsSortField;
const sortOrderStorageKey = LOCALSTORAGE_KEYS.resultsSortOrder;

export function Results() {
  const { t } = useTranslation();

  const [resultUploadDialogOpen, setResultUploadDialogOpen] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | undefined>();
  const [oneCustomerAvailable, setOneCustomerAvailable] = useState(false);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string | undefined>();
  const [selectedSiteName, setSelectedSiteName] = useState<string | undefined>();

  const companies = useCompaniesByCustomerId(selectedCustomer?.id);

  const companyId = useMemo(() => {
    if (companies.data && selectedCompanyName) {
      return companies.data?.find((c) => c.name === selectedCompanyName)?.id;
    }
  }, [companies.data, selectedCompanyName]);

  const sites = useSitesByCompanyId(companyId);

  const siteId = useMemo(() => {
    if (sites.data && selectedSiteName) {
      return sites.data?.find((c) => c.name === selectedSiteName)?.id;
    }
  }, [sites.data, selectedSiteName]);

  const [requestType, setRequestType] = useState<ResultListRequestType>();

  const [lazyState, setLazyState] = useState<DataTablePFSEvent>(() => {
    const field = (localStorage.getItem(sortFieldStorageKey) as string) ?? "time";
    const order = (localStorage.getItem(sortOrderStorageKey) as DataTableSortOrderType) ?? -1;
    const sort: DataTableMultiSortMetaType = [{ field: field, order: order }];

    return {
      ...INITIAL_LAZY_STATE,
      sortField: field,
      sortOrder: order,
      multiSortMeta: sort,
    };
  });

  const [reportModalData, setReportModalData] = useState<ReportModalData[]>([]);
  const [reportValuesList, setReportValuesList] = useState<ReportValues[]>([]);
  const [matchingReportModalData, setMatchingReportModalData] = useState<ReportModalData[]>([]);
  const [matchingReportValuesList, setMatchingReportValuesList] = useState<ReportValues[]>([]);

  const onFilter = (event: DataTablePFSEvent) => {
    event["first"] = 0;
    event["page"] = 1;
    setLazyState(event);
  };

  const onSort = (event: DataTablePFSEvent) => {
    event["page"] = lazyState.page;
    const field = event.multiSortMeta?.at(0)?.field as string;
    const order = event.multiSortMeta?.at(0)?.order ?? 0;
    localStorage.setItem(sortFieldStorageKey, field);
    order && localStorage.setItem(sortOrderStorageKey, order?.toString());
    event["sortField"] = field !== undefined ? field : "";
    event["sortOrder"] = order;
    setLazyState(event);
  };

  function onPage(event: DataTablePFSEvent) {
    event.page = (event.page ?? 0) + 1;
    setLazyState(event);
  }

  const handleCustomerSelect = useCallback(
    (customer?: Customer) => {
      lazyState["first"] = 0;
      lazyState["page"] = 1;
      setLazyState(lazyState);
      setSelectedCustomer(customer);
      setReportModalData([]);
      setReportValuesList([]);
      setMatchingReportValuesList([]);
      setMatchingReportModalData([]);
    },
    [lazyState],
  );

  const handleSingleCustomerAvailable = useCallback((customer: Customer) => {
    setSelectedCustomer(customer);
    setOneCustomerAvailable(true);
  }, []);

  const handleCompanyChange = useCallback(
    (selectedCompanyName: string) => {
      lazyState["first"] = 0;
      lazyState["page"] = 1;
      setLazyState(lazyState);
      setSelectedCompanyName(selectedCompanyName);
      setReportModalData([]);
      setReportValuesList([]);
      setMatchingReportValuesList([]);
      setMatchingReportModalData([]);
    },
    [lazyState],
  );

  const handleSiteChange = useCallback(
    (selectedSiteName: string) => {
      lazyState["first"] = 0;
      lazyState["page"] = 1;
      setLazyState(lazyState);
      setSelectedSiteName(selectedSiteName);
      setReportModalData([]);
      setReportValuesList([]);
      setMatchingReportValuesList([]);
      setMatchingReportModalData([]);
    },
    [lazyState],
  );

  const handleSnackbarClose = useCallback(() => {
    setShowSnackbar(false);
  }, []);

  const handleUploadButtonClick = useCallback(() => {
    setResultUploadDialogOpen(true);
  }, []);

  const handleUploadDialogClose = useCallback(() => {
    setResultUploadDialogOpen(false);
  }, []);

  const handleResultUploadSuccess = useCallback(() => {
    setResultUploadDialogOpen(false);
    setSnackbarSeverity("success");
    setSnackbarText(`${t("results.resultUploadSuccess")} ${t("resultResubmission.resubmittedIfError")}`);
    setShowSnackbar(true);
  }, [t]);

  function onGenerateAppendixSuccess() {
    setSnackbarSeverity("success");
    setSnackbarText(t("results.generateAppendixSuccess"));
    setShowSnackbar(true);
  }

  function onGenerateAppendixError() {
    setSnackbarSeverity("error");
    setSnackbarText(t("results.generateAppendixError"));
    setShowSnackbar(true);
  }

  function handleUpdateReportStatusSuccess() {
    setSnackbarText(t("results.updateReportStatusRemovedAnalysisSuccess"));
    setShowSnackbar(true);
    setReportValuesList([]);
    setMatchingReportValuesList([]);
  }

  function handleUpdateReportStatusError() {
    setSnackbarText(t("results.updateReportStatusRemovedAnalysisError"));
    setShowSnackbar(true);
  }

  const selectedTab = Object.values(TabLabels)[selectedTabIndex];

  useEffect(() => {
    if (selectedCustomer && selectedTab === TabLabels.AllResults) {
      setRequestType({ type: "byCustomer", customerId: selectedCustomer.id });
    } else if (selectedTab === TabLabels.MyCompleteResults) {
      setRequestType({ type: "completeByUser" });
    } else if (selectedCustomer && selectedTab === TabLabels.MyPartialResults) {
      setRequestType({
        type: "partialByUser",
        customerId: selectedCustomer.id,
      });
    } else {
      setRequestType(undefined);
    }
  }, [selectedCustomer, selectedTab]);

  const handleTabChange = useCallback((selectedIndex: number) => {
    setSelectedTabIndex(selectedIndex);
  }, []);

  return (
    <React.Fragment>
      <Box marginBottom={2}>
        <RouteBreadcrumbs />
      </Box>

      <TabView tabLabels={Object.values(TabLabels).map((key) => t(key))} onTabChange={handleTabChange}>
        <Toolbar
          className="mt-3"
          left={
            <>
              {!oneCustomerAvailable && (
                <CustomerDropdown
                  selectedCustomer={selectedCustomer}
                  onCustomerSelect={handleCustomerSelect}
                  onSingleCustomerAvailable={handleSingleCustomerAvailable}
                  showLabel
                />
              )}

              <PersistedCompanyDropdown
                customerId={selectedCustomer?.id}
                onCompanyChange={handleCompanyChange}
                showLabel
              />
              <PersistedSiteDropdown
                companyId={companyId}
                customerId={selectedCustomer?.id}
                onSiteChange={handleSiteChange}
                showLabel
              />
            </>
          }
        />
      </TabView>

      {selectedTab === TabLabels.MyPartialResults && !oneCustomerAvailable && (
        <CustomerDropdown
          selectedCustomer={selectedCustomer}
          onCustomerSelect={handleCustomerSelect}
          onSingleCustomerAvailable={handleSingleCustomerAvailable}
          showLabel
        />
      )}

      <ResultUploader
        selectedCustomer={selectedCustomer}
        open={resultUploadDialogOpen}
        onClose={handleUploadDialogClose}
        onUploadSuccess={handleResultUploadSuccess}
        onCustomerSelect={handleCustomerSelect}
      />

      {selectedTab === TabLabels.AllResults
        ? requestType &&
          requestType.type === "byCustomer" && (
            <AllResults
              lazyState={lazyState}
              requestType={requestType}
              companyId={companyId ?? 0}
              siteId={siteId ?? 0}
              selectedCustomer={selectedCustomer}
              reportModalData={reportModalData}
              reportValuesList={reportValuesList}
              matchingReportModalData={matchingReportModalData}
              matchingReportValuesList={matchingReportValuesList}
              onFilter={onFilter}
              onSort={onSort}
              onPage={onPage}
              handleUploadButtonClick={handleUploadButtonClick}
              onGenerateAppendixSuccess={onGenerateAppendixSuccess}
              onGenerateAppendixError={onGenerateAppendixError}
              setReportModalData={setReportModalData}
              setReportValuesList={setReportValuesList}
              setMatchingReportModalData={setMatchingReportModalData}
              setMatchingReportValuesList={setMatchingReportValuesList}
              handleUpdateReportStatusSuccess={handleUpdateReportStatusSuccess}
              handleUpdateReportStatusError={handleUpdateReportStatusError}
            />
          )
        : requestType &&
          requestType.type !== "byCustomer" && (
            <UserResults
              requestType={requestType}
              selectedCustomer={selectedCustomer}
              selectedTab={selectedTab}
              companyId={companyId ?? 0}
              siteId={siteId ?? 0}
              handleUploadButtonClick={handleUploadButtonClick}
            />
          )}

      <Snackbar open={showSnackbar} onClose={handleSnackbarClose}>
        <Alert severity={snackbarSeverity} variant="filled">
          {snackbarText}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}
